import type { DifficultyLevel } from '@/types'

export interface CoursePreview {
  id: string
  title: string
  description: string
  level: DifficultyLevel
  color: string
  icon: string
  moduleCount: number
  estimatedHours: number
  tags: string[]
  comingSoon?: boolean
}

const courseListRaw: CoursePreview[] = [
  {
    id: 'ai-ml-foundations',
    title: 'AI & ML Foundations',
    description: 'Start your AI journey with the fundamentals of artificial intelligence, machine learning, and deep learning.',
    level: 'beginner',
    color: '#10b981',
    icon: '🧠',
    moduleCount: 7,
    estimatedHours: 12,
    tags: ['AI Basics', 'Machine Learning', 'Neural Networks'],
  },
  {
    id: 'nlp-basics',
    title: 'Natural Language Processing',
    description: 'Understand how machines process human language, from text processing to transformers.',
    level: 'beginner',
    color: '#3b82f6',
    icon: '💬',
    moduleCount: 5,
    estimatedHours: 8,
    tags: ['NLP', 'Tokenization', 'Transformers'],
  },
  {
    id: 'python-for-ai',
    title: 'Python for AI (In-Browser)',
    description: 'Master Python essentials for AI with hands-on exercises you can run right in your browser.',
    level: 'beginner',
    color: '#f59e0b',
    icon: '🐍',
    moduleCount: 5,
    estimatedHours: 10,
    tags: ['Python', 'NumPy', 'Scikit-learn'],
  },
  {
    id: 'ai-ethics',
    title: 'AI Ethics & Responsible AI',
    description: 'Explore bias, fairness, privacy, and the societal impact of AI systems.',
    level: 'beginner',
    color: '#ec4899',
    icon: '⚖️',
    moduleCount: 5,
    estimatedHours: 6,
    tags: ['Ethics', 'Bias', 'Regulation'],
  },
  {
    id: 'llm-deep-dive',
    title: 'Large Language Models Deep Dive',
    description: 'Go deep into how LLMs work — architecture, training, tokenization, and prompt engineering.',
    level: 'intermediate',
    color: '#8b5cf6',
    icon: '🔮',
    moduleCount: 6,
    estimatedHours: 14,
    tags: ['LLMs', 'Transformers', 'Prompting'],
  },
  {
    id: 'ai-agents',
    title: 'AI Agents & Autonomous Systems',
    description: 'Build intelligent agents with tool use, memory, and multi-agent architectures.',
    level: 'intermediate',
    color: '#06b6d4',
    icon: '🤖',
    moduleCount: 5,
    estimatedHours: 12,
    tags: ['Agents', 'Tool Use', 'MCP'],
  },
  {
    id: 'rag-knowledge',
    title: 'RAG & Knowledge Systems',
    description: 'Master retrieval-augmented generation from basic pipelines to advanced techniques.',
    level: 'intermediate',
    color: '#14b8a6',
    icon: '🔍',
    moduleCount: 6,
    estimatedHours: 14,
    tags: ['RAG', 'Embeddings', 'Vector DB'],
  },
  {
    id: 'prompt-engineering',
    title: 'Prompt Engineering Professional',
    description: 'Become an expert at crafting prompts — from system prompts to prompt injection defense.',
    level: 'intermediate',
    color: '#f97316',
    icon: '✍️',
    moduleCount: 6,
    estimatedHours: 10,
    tags: ['Prompts', 'Few-Shot', 'Security'],
  },
  {
    id: 'ai-app-patterns',
    title: 'AI Application Patterns',
    description: 'Learn common patterns for building AI-powered apps: chatbots, document processing, code gen.',
    level: 'intermediate',
    color: '#84cc16',
    icon: '🏗️',
    moduleCount: 5,
    estimatedHours: 10,
    tags: ['Chatbots', 'Summarization', 'Code Gen'],
  },
  {
    id: 'local-llms',
    title: 'Local LLMs & Self-Hosting',
    description: 'Run models on your own hardware with Ollama, llama.cpp, and vLLM.',
    level: 'advanced',
    color: '#ef4444',
    icon: '🖥️',
    moduleCount: 5,
    estimatedHours: 12,
    tags: ['Ollama', 'Quantization', 'Self-Hosting'],
  },
  {
    id: 'model-architecture',
    title: 'Advanced Model Architecture',
    description: 'Deep dive into MoE, attention mechanisms, positional encodings, and state space models.',
    level: 'advanced',
    color: '#a855f7',
    icon: '🏛️',
    moduleCount: 5,
    estimatedHours: 14,
    tags: ['MoE', 'Attention', 'SSM'],
  },
  {
    id: 'training-at-scale',
    title: 'Training & Fine-tuning at Scale',
    description: 'Pre-training, distributed training, RLHF, DPO, and synthetic data generation.',
    level: 'advanced',
    color: '#e11d48',
    icon: '⚡',
    moduleCount: 6,
    estimatedHours: 16,
    tags: ['Training', 'RLHF', 'LoRA'],
  },
  {
    id: 'production-ai',
    title: 'Production AI Systems',
    description: 'Design reliable, cost-effective AI systems for production — caching, routing, monitoring.',
    level: 'advanced',
    color: '#0ea5e9',
    icon: '🚀',
    moduleCount: 6,
    estimatedHours: 14,
    tags: ['Production', 'Cost', 'Monitoring'],
  },
  {
    id: 'computer-vision',
    title: 'Computer Vision + Multimodal AI',
    description: 'Vision transformers, multimodal models, image generation, and audio/speech.',
    level: 'advanced',
    color: '#d946ef',
    icon: '👁️',
    moduleCount: 5,
    estimatedHours: 12,
    tags: ['Vision', 'Multimodal', 'Diffusion'],
  },
  {
    id: 'advanced-rag',
    title: 'Advanced RAG & Knowledge Graphs',
    description: 'Graph RAG, hybrid retrieval, multi-modal RAG, and agentic RAG patterns.',
    level: 'advanced',
    color: '#22d3ee',
    icon: '🕸️',
    moduleCount: 5,
    estimatedHours: 12,
    tags: ['GraphRAG', 'Hybrid', 'Agentic RAG'],
  },
  {
    id: 'path-to-agi',
    title: 'The Path to AGI',
    description: 'Scaling laws, test-time compute, world models, and the frontier of AI capability.',
    level: 'expert',
    color: '#fbbf24',
    icon: '🌟',
    moduleCount: 5,
    estimatedHours: 10,
    tags: ['AGI', 'Scaling', 'Reasoning'],
  },
  {
    id: 'ai-safety',
    title: 'AI Safety & Alignment',
    description: 'The alignment problem, interpretability, constitutional AI, and governance.',
    level: 'expert',
    color: '#f43f5e',
    icon: '🛡️',
    moduleCount: 6,
    estimatedHours: 12,
    tags: ['Safety', 'Alignment', 'Interpretability'],
  },
  {
    id: 'research-frontiers',
    title: 'Research Frontiers',
    description: 'Cutting-edge research: reasoning, continual learning, efficient architectures, emergent abilities.',
    level: 'expert',
    color: '#7c3aed',
    icon: '🔬',
    moduleCount: 6,
    estimatedHours: 14,
    tags: ['Research', 'Reasoning', 'Efficiency'],
  },
  {
    id: 'building-ai-companies',
    title: 'Building AI Companies',
    description: 'AI product strategy, competitive moats, pricing, hiring, and fundraising.',
    level: 'expert',
    color: '#059669',
    icon: '💼',
    moduleCount: 5,
    estimatedHours: 8,
    tags: ['Strategy', 'Business', 'Startups'],
  },
  {
    id: 'capstone-projects',
    title: 'Hands-On Capstone Projects',
    description: 'Build real projects: RAG chatbot, fine-tuned model, multi-agent system, and more.',
    level: 'expert',
    color: '#6366f1',
    icon: '🎯',
    moduleCount: 5,
    estimatedHours: 20,
    tags: ['Projects', 'Hands-On', 'Portfolio'],
  },
  {
    id: 'build-your-own-llm',
    title: 'Build Your Own LLM from Scratch',
    description: 'The ultimate step-by-step guide to building, training, and deploying your own large language model.',
    level: 'expert',
    color: '#dc2626',
    icon: '🏗️',
    moduleCount: 8,
    estimatedHours: 30,
    tags: ['LLM', 'Pre-training', 'PyTorch', 'RLHF', 'Deployment'],
  },
  {
    id: 'mcp-zero-to-hero',
    title: 'MCP Zero to Hero: Build Your Own MCP Server',
    description: 'The definitive course on Model Context Protocol. Build 5 production MCP servers from scratch — database, scraper, code executor, and more.',
    level: 'expert',
    color: '#7c3aed',
    icon: '🔌',
    moduleCount: 12,
    estimatedHours: 50,
    tags: ['MCP', 'Protocol', 'Tool Use', 'TypeScript', 'Python', 'Enterprise'],
    comingSoon: false,
  },
  {
    id: 'ai-for-hr',
    title: 'AI for HR & Talent Acquisition — Zero to Expert',
    description: 'The most comprehensive AI course for HR leaders and talent acquisition teams. Master sourcing, screening, analytics, employee lifecycle, compliance, DEI, and strategic transformation.',
    level: 'beginner',
    color: '#7c3aed',
    icon: '👥',
    moduleCount: 26,
    estimatedHours: 62,
    tags: ['HR', 'Talent Acquisition', 'People Analytics', 'Recruitment AI', 'HR Tech'],
  },
  {
    id: 'ai-stock-market',
    title: 'AI for Stock Market — Zero to Pro Trader',
    description: 'The most comprehensive AI-powered stock market course for Indian investors and traders. Master fundamental analysis, technical analysis, options, algo trading, and portfolio management with AI.',
    level: 'beginner',
    color: '#10b981',
    icon: '📈',
    moduleCount: 44,
    estimatedHours: 85,
    tags: ['Stock Market', 'Trading', 'Options', 'Algo Trading', 'NSE', 'Python'],
  },
  {
    id: 'ai-for-founders',
    title: 'AI for Founders & CEOs — The Decision Maker\'s Playbook',
    description: 'The definitive AI course for founders, CEOs, and CXOs. Master AI strategy, product thinking, team building, fundraising, and operations. No coding required.',
    level: 'beginner',
    color: '#f59e0b',
    icon: '🏢',
    moduleCount: 31,
    estimatedHours: 52,
    tags: ['Founders', 'CEO', 'AI Strategy', 'Business', 'Startups', 'Leadership'],
  },
  {
    id: 'ai-for-creators',
    title: 'AI for Content Creators & Social Media Managers',
    description: 'The most comprehensive AI course for creators and SM managers. 500+ prompt templates, platform playbooks, and monetization strategies.',
    level: 'beginner',
    color: '#ec4899',
    icon: '🎬',
    moduleCount: 45,
    estimatedHours: 70,
    tags: ['Content Creation', 'Social Media', 'Instagram', 'YouTube', 'LinkedIn', 'Creator Economy'],
  },
  {
    id: 'voice-ai-agents',
    title: 'Voice AI & Conversational Agents',
    description: 'Build 7 real voice/chat bots. Master ASR, TTS, LLM engines, telephony, WhatsApp bots, and Indian language support.',
    level: 'intermediate',
    color: '#8b5cf6',
    icon: '🎙️',
    moduleCount: 48,
    estimatedHours: 80,
    tags: ['Voice AI', 'Chatbots', 'Conversational AI', 'ASR', 'TTS', 'WhatsApp', 'Telephony'],
  },
  {
    id: 'ai-for-students',
    title: 'AI for College Students — Your Unfair Advantage',
    description: 'The ultimate AI course for Indian college students. Master AI for academics, placements, projects, career, and daily life.',
    level: 'beginner',
    color: '#06b6d4',
    icon: '🎓',
    moduleCount: 45,
    estimatedHours: 60,
    tags: ['Students', 'College', 'Placements', 'Exams', 'Projects', 'Career'],
  },
  {
    id: 'ai-for-sales',
    title: 'AI for Sales Teams — Close More, Sell Faster',
    description: 'Master AI-powered prospecting, outreach, qualification, pipeline management, negotiation, and forecasting. B2B, SaaS, enterprise, and SMB playbooks.',
    level: 'beginner',
    color: '#f97316',
    icon: '🎯',
    moduleCount: 35,
    estimatedHours: 62,
    tags: ['Sales', 'B2B', 'Pipeline', 'CRM', 'Prospecting', 'Outreach', 'Revenue'],
  },
  {
    id: 'fine-tuning-llms',
    title: 'Fine-Tuning LLMs on Custom Data',
    description: 'Master LoRA, QLoRA, SFT, RLHF, DPO. Build 5 real fine-tuned models. Deploy with vLLM. From zero to production.',
    level: 'advanced',
    color: '#dc2626',
    icon: '🔧',
    moduleCount: 36,
    estimatedHours: 65,
    tags: ['Fine-Tuning', 'LLM', 'LoRA', 'QLoRA', 'RLHF', 'DPO', 'Hugging Face'],
  },
  {
    id: 'ai-ethical-hacking',
    title: 'AI for Ethical Hacking & Cybersecurity — Zero to Red Team',
    description: 'The most comprehensive AI-powered ethical hacking course. Master recon, web hacking, network exploitation, Active Directory, and blue team defense with AI.',
    level: 'intermediate',
    color: '#ef4444',
    icon: '🛡️',
    moduleCount: 85,
    estimatedHours: 120,
    tags: ['Ethical Hacking', 'Cybersecurity', 'Penetration Testing', 'Bug Bounty', 'AI Security', 'OSINT', 'Web Hacking', 'Network Security'],
  },
  {
    id: 'ai-mobile-development',
    title: 'AI for Mobile Application Development — Zero to Expert',
    description: 'Master Flutter, iOS, and Android with AI. Build 5 production AI-powered apps. Ship to Play Store and App Store. From complete beginner to shipping paid apps.',
    level: 'beginner',
    color: '#06b6d4',
    icon: '📱',
    moduleCount: 85,
    estimatedHours: 130,
    tags: ['Mobile Development', 'Flutter', 'iOS', 'Android', 'Swift', 'Kotlin', 'AI Apps', 'Dart', 'Firebase'],
  },
  {
    id: 'ai-web-development',
    title: 'AI for Website Development — The Complete No-Code to Expert Course',
    description: "World's most detailed AI web dev course for non-coders. Master Cursor, v0, Lovable, Bolt, Claude Code. 500+ prompts. Build 5 production websites from scratch.",
    level: 'beginner',
    color: '#8b5cf6',
    icon: '🌐',
    moduleCount: 100,
    estimatedHours: 150,
    tags: ['Web Development', 'AI Tools', 'No-Code', 'React', 'Next.js', 'Cursor', 'v0', 'Lovable', 'Prompts', 'Tailwind', 'Supabase'],
  },
  {
    id: 'ai-qa-testing',
    title: 'AI for QA & Testing — Complete Zero to Expert Course',
    description: 'Master Selenium, Cypress, Playwright, Appium, API, performance, security testing with AI. Build 6 production test frameworks. Ready for ₹15-45 LPA SDET roles.',
    level: 'beginner',
    color: '#10b981',
    icon: '🐛',
    moduleCount: 100,
    estimatedHours: 140,
    tags: ['QA', 'Testing', 'Selenium', 'Cypress', 'Playwright', 'Appium', 'API Testing', 'Automation', 'SDET', 'AI Testing'],
  },
  {
    id: 'ai-data-science',
    title: 'AI for Data Science & Analytics — Complete Zero to Expert Course',
    description: 'Master Python, SQL, Power BI, Tableau, ML (XGBoost), DL, and AI tools (Julius, Cursor, Claude). Build 3 production capstones. Ready for ₹6-40+ LPA DS/DA roles.',
    level: 'beginner',
    color: '#3b82f6',
    icon: '📊',
    moduleCount: 100,
    estimatedHours: 140,
    tags: ['Data Science', 'Analytics', 'Python', 'Pandas', 'SQL', 'Machine Learning', 'Power BI', 'Tableau', 'XGBoost', 'AI Tools'],
  },
  {
    id: 'ai-devops-cloud',
    title: 'AI for DevOps & Cloud Engineers — Complete Zero to Expert Course',
    description: 'Master Linux, Docker, Kubernetes, AWS, GCP, Azure, Terraform, CI/CD, observability, AIOps, DevSecOps. Build 6 production capstones. Ready for ₹15-70 LPA SRE/DevOps roles.',
    level: 'beginner',
    color: '#0ea5e9',
    icon: '☁️',
    moduleCount: 100,
    estimatedHours: 150,
    tags: ['DevOps', 'Cloud', 'AWS', 'GCP', 'Azure', 'Kubernetes', 'Docker', 'Terraform', 'CI/CD', 'SRE', 'AIOps'],
  },
  {
    id: 'ai-embedded-iot',
    title: 'AI for Embedded & IoT Developers — Edge AI Engineering Course',
    description: 'Master Arduino, ESP32, Pi, STM32, Jetson, TinyML, FreeRTOS, LoRa, MQTT, AWS IoT, and Edge AI. Run LLMs on edge. Build 6 production capstones. Ready for ₹4-80+ LPA embedded/Edge AI roles.',
    level: 'intermediate',
    color: '#14b8a6',
    icon: '🔌',
    moduleCount: 100,
    estimatedHours: 150,
    tags: ['Embedded Systems', 'IoT', 'TinyML', 'Edge AI', 'Arduino', 'ESP32', 'Raspberry Pi', 'STM32', 'Jetson', 'FreeRTOS', 'LoRa', 'MQTT'],
  },
  {
    id: 'solo-ai-ceo',
    title: 'Solo AI CEO — Build a ₹1 Cr Business with 12 AI Employees',
    description: "Flagship course. Set up 12 specialized AI employees handling 95% of operations. Master CrewAI, LangGraph, n8n, Claude. By Module 100 you have a running ₹10L-1Cr/year solo business with 4-hour workweeks.",
    level: 'intermediate',
    color: '#fbbf24',
    icon: '👑',
    moduleCount: 100,
    estimatedHours: 130,
    tags: ['Solo Founder', 'AI Agents', 'CrewAI', 'LangGraph', 'n8n', 'Solopreneur', 'AI Business', 'Automation', 'Indian Founders'],
  },
]

export const courseList: CoursePreview[] = [...courseListRaw].reverse()
